// src-lit/components/product/kd-quantity-input.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import {
  EVENT_NAMES,
  type QuantityClampDetail,
  type QuantityClampReason,
} from '../../constants/events';

const SELECTOR = {
  INPUT: 'input[type="number"]',
  MINUS: 'button[name="minus"]',
  PLUS: 'button[name="plus"]',
} as const;

const DEFAULT_MIN = 1;

/**
 * @element kd-quantity-input
 * @description 数量入力の +/- ボタン制御。
 *   ボタンクリックで input の値を増減し、min/max を尊重する。
 *   現在値が min/max に達したボタンは disabled にして見た目を切り替える。
 *   ユーザー手入力で min/max を逸脱した値は change（blur）時にクランプする。
 */
@customElement('kd-quantity-input')
export class KdQuantityInput extends LitElement {
  createRenderRoot() {
    return this;
  }

  private input: HTMLInputElement | null = null;

  private minusButton: HTMLButtonElement | null = null;

  private plusButton: HTMLButtonElement | null = null;

  private attrObserver: MutationObserver | null = null;

  private isClamping = false;

  private boundOnMinus = this.onMinus.bind(this);

  private boundOnPlus = this.onPlus.bind(this);

  private boundOnInput = this.onInput.bind(this);

  private boundOnChange = this.onChange.bind(this);

  private boundSyncButtons = this.syncButtonsState.bind(this);

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.input = this.querySelector<HTMLInputElement>(SELECTOR.INPUT);
    this.minusButton = this.querySelector<HTMLButtonElement>(SELECTOR.MINUS);
    this.plusButton = this.querySelector<HTMLButtonElement>(SELECTOR.PLUS);
    this.minusButton?.addEventListener('click', this.boundOnMinus);
    this.plusButton?.addEventListener('click', this.boundOnPlus);
    this.input?.addEventListener('input', this.boundOnInput);
    this.input?.addEventListener('change', this.boundOnChange);
    if (this.input) {
      // 外部コード（バリアント切替など）が input.max / input.min を書き換えた場合、
      // change/input イベントは発火しないため属性変更を直接監視して再同期する。
      this.attrObserver = new MutationObserver(this.boundSyncButtons);
      this.attrObserver.observe(this.input, {
        attributes: true,
        attributeFilter: ['min', 'max'],
      });
    }
    this.syncButtonsState();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.minusButton?.removeEventListener('click', this.boundOnMinus);
    this.plusButton?.removeEventListener('click', this.boundOnPlus);
    this.input?.removeEventListener('input', this.boundOnInput);
    this.input?.removeEventListener('change', this.boundOnChange);
    this.attrObserver?.disconnect();
    this.attrObserver = null;
    this.input = null;
    this.minusButton = null;
    this.plusButton = null;
  }

  /**
   * input の現在値・min・max を数値として取り出す。
   * 不正値や空文字は安全側にフォールバックする（min=1, max=Infinity, current=NaN）。
   */
  private getBounds(): { current: number; min: number; max: number } {
    if (!this.input) return { current: NaN, min: DEFAULT_MIN, max: Infinity };
    const currentRaw = Number(this.input.value);
    const current = Number.isFinite(currentRaw) ? currentRaw : NaN;
    const minRaw = Number(this.input.min);
    const min = this.input.min !== '' && Number.isFinite(minRaw) ? minRaw : DEFAULT_MIN;
    const maxRaw = Number(this.input.max);
    const max = this.input.max !== '' && Number.isFinite(maxRaw) ? maxRaw : Infinity;
    return { current, min, max };
  }

  private onMinus(): void {
    if (!this.input) return;
    const { current, min } = this.getBounds();
    const base = Number.isFinite(current) ? current : min;
    if (base <= min) return;
    this.input.value = String(base - 1);
    this.dispatchValueChange();
  }

  private onPlus(): void {
    if (!this.input) return;
    const { current, min, max } = this.getBounds();
    const base = Number.isFinite(current) ? current : min;
    if (base >= max) return;
    this.input.value = String(base + 1);
    this.dispatchValueChange();
  }

  /** ボタン操作で値を変更した際にネイティブ操作と同様 input → change の順で発火させる */
  private dispatchValueChange(): void {
    if (!this.input) return;
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * 入力途中は値を書き換えず disabled 表示だけ更新する（編集体験を阻害しない）。
   * 値が再び有効な範囲に戻ったら QUANTITY_VALID を発火し、外側のエラー表示を解消できるようにする。
   */
  private onInput(): void {
    this.syncButtonsState();
    if (this.isValueInBounds()) {
      this.dispatchValidEvent();
    }
  }

  /** 入力確定（blur 等）時に min/max を超える値をクランプし、必要なら再通知する */
  private onChange(): void {
    if (this.isClamping) return;
    const result = this.clampToBounds();
    if (result) {
      this.dispatchClampEvent(result.reason, result.attempted, result.clampedTo);
      this.isClamping = true;
      this.input?.dispatchEvent(new Event('change', { bubbles: true }));
      this.isClamping = false;
    } else {
      // 範囲内で確定した場合もエラー解消の合図を出す
      this.dispatchValidEvent();
    }
    this.syncButtonsState();
  }

  /**
   * 現在値が min/max を逸脱していればクランプして書き戻す。
   * 補正が走った場合は { reason, attempted, clampedTo } を返し、不要なら null を返す。
   * 空文字や NaN は min にリセットする。
   */
  private clampToBounds(): { reason: QuantityClampReason; attempted: string; clampedTo: number } | null {
    if (!this.input) return null;
    const attempted = this.input.value;
    const { current, min, max } = this.getBounds();
    if (!Number.isFinite(current)) {
      this.input.value = String(min);
      return { reason: 'invalid', attempted, clampedTo: min };
    }
    const clamped = Math.min(Math.max(current, min), max);
    if (clamped !== current) {
      this.input.value = String(clamped);
      const reason: QuantityClampReason = current > max ? 'max-exceeded' : 'invalid';
      return { reason, attempted, clampedTo: clamped };
    }
    return null;
  }

  /** 現在値が min/max の範囲内かつ有限数か */
  private isValueInBounds(): boolean {
    if (!this.input) return false;
    const { current, min, max } = this.getBounds();
    return Number.isFinite(current) && current >= min && current <= max;
  }

  private dispatchClampEvent(
    reason: QuantityClampReason,
    attempted: string,
    clampedTo: number,
  ): void {
    const { max } = this.getBounds();
    const detail: QuantityClampDetail = {
      reason,
      max: Number.isFinite(max) ? max : null,
      attempted,
      clampedTo,
    };
    this.dispatchEvent(
      new CustomEvent<QuantityClampDetail>(EVENT_NAMES.PRODUCT.QUANTITY_CLAMP, {
        bubbles: true,
        composed: true,
        detail,
      }),
    );
  }

  private dispatchValidEvent(): void {
    this.dispatchEvent(
      new CustomEvent(EVENT_NAMES.PRODUCT.QUANTITY_VALID, {
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** 現在値と min/max を比較して各ボタンの disabled 属性を更新する */
  private syncButtonsState(): void {
    if (!this.input) return;
    const { current, min, max } = this.getBounds();
    const base = Number.isFinite(current) ? current : min;
    if (this.minusButton) this.minusButton.disabled = base <= min;
    if (this.plusButton) this.plusButton.disabled = base >= max;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-quantity-input': KdQuantityInput;
  }
}
